import { Map } from "immutable"
import { Observable } from "rxjs/Observable"
import { push } from "react-router-redux"

import { getImageAPIFunction } from "../../api/image-api"
import {
    mapRequestError,
    isListCached,
    isListCountCached,
    isEntityCached,
    getIn
} from "./epic-utils"
import { postPlaceholder } from "../../api/placeholder-api"
import { serialiseSearchCriteria } from "../../reducers/paging/paging-reducer-utils"
import { unauthenticated } from "../../actions/authentication-actions"

export const requestListEpic = (
    type,
    getListAPI,
    pagingAccessor,
    responseCreator,
    cachedCreator,
    errorCreator
) => (action$, { getState }) =>
    action$
        .ofType(type)
        .mergeMap(
            ({
                searchCriteria,
                nextOffset,
                limit,
                isPossibleNextPage,
                isInvalidating,
                lastUpdatedAt,
                callbackActionCreator
            }) => {
                const stringSearchCriteria = serialiseSearchCriteria(
                    searchCriteria
                )
                const paging = getIn(getState().paging, pagingAccessor)
                const {
                    data,
                    lastUpdatedAt: storedLastUpdatedAt,
                    isPossibleNextPage: storedIsPossibleNextPage
                } = paging[stringSearchCriteria]
                isPossibleNextPage =
                    isPossibleNextPage !== null &&
                    isPossibleNextPage !== undefined
                        ? isPossibleNextPage
                        : storedIsPossibleNextPage
                if (
                    isListCached(
                        isInvalidating,
                        data,
                        isPossibleNextPage,
                        nextOffset,
                        limit,
                        lastUpdatedAt || storedLastUpdatedAt
                    )
                ) {
                    return Observable.of(
                        cachedCreator(
                            data,
                            limit,
                            data.length,
                            searchCriteria,
                            callbackActionCreator
                        )
                    )
                } else {
                    // Not cached, so perform additional action of network request
                    const authToken = getState().authentication.token
                    return getListAPI(
                        authToken,
                        searchCriteria.merge({ limit, offset: nextOffset })
                    )
                        .map(data =>
                            responseCreator(
                                data,
                                limit,
                                nextOffset,
                                searchCriteria,
                                isInvalidating,
                                callbackActionCreator
                            )
                        )
                        .catch(error =>
                            Observable.of(
                                mapRequestError(
                                    error,
                                    unauthenticated,
                                    errorCreator,
                                    searchCriteria,
                                    callbackActionCreator
                                )
                            )
                        )
                }
            }
        )

export const requestListCountEpic = (
    type,
    getListCountAPI,
    pagingAccessor,
    responseCreator,
    cachedCreator,
    errorCreator
) => (action$, { getState }) =>
    action$
        .ofType(type)
        .mergeMap(
            ({
                searchCriteria,
                isInvalidating,
                lastUpdatedAt,
                callbackActionCreator
            }) => {
                const stringSearchCriteria = serialiseSearchCriteria(
                    searchCriteria
                )
                const paging = getIn(getState().paging, pagingAccessor)
                const { count, lastUpdatedAt: storedLastUpdatedAt } = paging[
                    stringSearchCriteria
                ]
                if (
                    isListCountCached(
                        isInvalidating,
                        count,
                        lastUpdatedAt || storedLastUpdatedAt
                    )
                ) {
                    return Observable.of(
                        cachedCreator(
                            { count },
                            0,
                            0,
                            searchCriteria,
                            callbackActionCreator
                        )
                    )
                } else {
                    // Not cached, so perform additional action of network request
                    const authToken = getState().authentication.token
                    return getListCountAPI(authToken, searchCriteria)
                        .map(data =>
                            responseCreator(
                                data,
                                0,
                                0,
                                searchCriteria,
                                isInvalidating,
                                callbackActionCreator
                            )
                        )
                        .catch(error =>
                            Observable.of(
                                mapRequestError(
                                    error,
                                    unauthenticated,
                                    errorCreator,
                                    searchCriteria,
                                    callbackActionCreator
                                )
                            )
                        )
                }
            }
        )

export const requestListFilterEpic = (
    type,
    requestListActionCreator,
    presentationAccessor,
    pagingAccessor = presentationAccessor,
    filterToAPIFunction
) => (action$, { getState }) =>
    action$
        .ofType(type)
        .map(() =>
            listFilterHandler(
                getState,
                requestListActionCreator,
                presentationAccessor,
                pagingAccessor,
                filterToAPIFunction
            )
        )

// Essentially the same as above, but debounced
export const requestListSearchEpic = (
    type,
    requestListActionCreator,
    presentationAccessor,
    pagingAccessor = presentationAccessor,
    filterToAPIFunction
) => (action$, { getState }) =>
    action$
        .ofType(type)
        .debounceTime(500)
        .map(() =>
            listFilterHandler(
                getState,
                requestListActionCreator,
                presentationAccessor,
                pagingAccessor,
                filterToAPIFunction
            )
        )

function listFilterHandler(
    getState,
    requestListActionCreator,
    presentationAccessor,
    pagingAccessor,
    filterToAPIFunction
) {
    const { presentation, paging } = getState()
    const serialisedSearchCriteria = getIn(presentation, [
        presentationAccessor,
        "searchCriteria"
    ])
    const { searchFilterModel: searchCriteria } = getIn(
        presentation,
        presentationAccessor
    )
    const pageRequest =
        getIn(paging, [pagingAccessor, serialisedSearchCriteria]) || {}
    return requestListActionCreator({
        ...pageRequest,
        nextOffset: 0,
        offset: 0,
        searchCriteria
    })
}

export const requestItemEpic = (
    actionType,
    getItemAPI,
    entityAccessor,
    hydrateFunction,
    hydratingFieldNames,
    responseCreator,
    cachedCreator,
    errorCreator
) => (action$, { getState }) =>
    action$
        .filter(({ type }) => type === actionType)
        .mergeMap(
            requestItemHandler(
                getState,
                getItemAPI,
                entityAccessor,
                hydrateFunction,
                hydratingFieldNames,
                responseCreator,
                cachedCreator,
                errorCreator
            )
        )

export const requestItemHandler = (
    getState,
    getItemAPI,
    entityAccessor,
    hydrateFunction,
    hydratingFieldNames,
    responseCreator,
    cachedCreator,
    errorCreator
) => ({ id, isInvalidating, callbackActionCreator }) => {
    const { entities, authentication } = getState()
    const hydratingFields = hydratingFieldNames.map(field => entities[field])
    const storedItem = entities[entityAccessor][id]
    if (isEntityCached(isInvalidating, storedItem))
        return Observable.of(
            cachedCreator(
                hydrateFunction(storedItem, ...hydratingFields),
                callbackActionCreator
            )
        )
    else
        return getItemAPI(authentication.token, id)
            .map(data =>
                responseCreator(data, isInvalidating, callbackActionCreator)
            )
            .catch(error =>
                Observable.of(
                    mapRequestError(
                        error,
                        unauthenticated,
                        errorCreator,
                        id,
                        callbackActionCreator
                    )
                )
            )
}

// Concurrently requests for adding placeholders with item save request
// and merges the multiple outputs after the fact
export const requestItemSaveWithPlaceholdersEpic = (
    type,
    itemSaveRequestHandler,
    relatedItemType
) => (action$, { getState }) =>
    action$.ofType(type).mergeMap(action => {
        let { item, id } = action
        let { placeholders_add, ...itemWithoutPlaceholders } = item
        action = {
            ...action,
            item: itemWithoutPlaceholders
        }
        if (placeholders_add) {
            const { token } = getState().authentication
            let result$ = itemSaveRequestHandler(getState, action)
            if (id) {
                const placeholdersAddRequests = placeholders_add.map(
                    placeholder =>
                        postPlaceholder(
                            token,
                            placeholder,
                            new Map({ [relatedItemType]: id })
                        )
                )
                result$ = Observable.forkJoin([
                    result$,
                    ...placeholdersAddRequests
                ])
            } else {
                result$ = result$.mergeMap(itemSaveActionResponse => {
                    const placeholdersAddRequests = placeholders_add.map(
                        placeholder =>
                            postPlaceholder(
                                token,
                                placeholder,
                                new Map({
                                    [relatedItemType]:
                                        itemSaveActionResponse.data.id
                                })
                            )
                    )
                    return Observable.forkJoin([
                        Observable.of(itemSaveActionResponse),
                        ...placeholdersAddRequests
                    ])
                })
            }
            return result$.map(
                ([itemSaveActionResponse, ...placeholderAddAPIResponses]) => {
                    const { data } = itemSaveActionResponse
                    const { placeholders } = data
                    const otherPlaceholders = placeholders.filter(
                        ({ id }) =>
                            !placeholderAddAPIResponses.some(
                                ({ id: addedId }) => id === addedId
                            )
                    )
                    return {
                        ...itemSaveActionResponse,
                        data: {
                            ...data,
                            placeholders: placeholderAddAPIResponses.concat(
                                otherPlaceholders
                            )
                        }
                    }
                }
            )
        } else return itemSaveRequestHandler(getState, action)
    })

function getSearchCriteriaFromAction(action) {
    if (action.searchCriteria) {
        return action.searchCriteria
    } else if (action.data) {
        return action.data.searchCriteria
    } else {
        return null
    }
}

// Automatically request the first item on the list
// if the search criteria is new (and there is data)
export const requestTopItemFromListResponseEpic = (
    types,
    itemRequestActionCreator,
    presentationAccessor,
    pagingAccessor = presentationAccessor,
    defaultSearchCriteria = new Map()
) => (action$, { getState }) =>
    (Array.isArray(types) ? action$.ofType(...types) : action$.ofType(types))
        .distinctUntilChanged((action1, action2) => {
            const criteria1 = getSearchCriteriaFromAction(action1)
            const criteria2 = getSearchCriteriaFromAction(action2)
            return !(criteria1 && criteria2) || criteria1.equals(criteria2)
        })
        .map(action => {
            const state = getState()
            const paging = getIn(state.paging, pagingAccessor)
            const presentation = getIn(state.presentation, presentationAccessor)
            return paging[presentation.searchCriteria] || {}
        })
        .filter(({ data, searchCriteria }) => {
            // Is there data to select from?
            if (!data || !data.length) {
                return false
            }
            const presentation = getIn(
                getState().presentation,
                presentationAccessor
            )
            // Is there no currently active item
            // or is the user searching / filtering for something ?
            return (
                !presentation.activeId ||
                !defaultSearchCriteria.equals(searchCriteria)
            )
        })
        .map(({ data }) => itemRequestActionCreator(data[0], false))

export const requestItemSaveEpic = (
    type,
    putItemAPI,
    postItemAPI,
    imageAPIAccessor,
    responseCreator,
    errorCreator
) => {
    const requestItemSave = requestItemSaveHandler(
        putItemAPI,
        postItemAPI,
        imageAPIAccessor,
        responseCreator,
        errorCreator
    )
    return (action$, { getState }) =>
        action$
            .ofType(type)
            .mergeMap(action => requestItemSave(getState, action))
}

export const requestItemSaveHandler = (
    putItemAPI,
    postItemAPI,
    imageAPIAccessor,
    responseCreator,
    errorCreator
) => (getState, { id, item, image, callbackActionCreator }) => {
    const { token } = getState().authentication
    let result$ = selectSaveAPI(id, item, token, putItemAPI, postItemAPI)
    if (image !== undefined) {
        if (id) {
            result$ = Observable.combineLatest(
                result$,
                getImageAPIFunction(image)(token, imageAPIAccessor, id, image)
            ).map(([entityData, imageData]) => ({
                ...entityData,
                ...imageData
            }))
        } else {
            result$ = result$.mergeMap(data =>
                getImageAPIFunction(image)(
                    token,
                    imageAPIAccessor,
                    data.id,
                    image
                ).map(imageData => ({
                    ...data,
                    ...imageData
                }))
            )
        }
    }
    return result$
        .map(data => responseCreator(data, true, callbackActionCreator))
        .catch(error =>
            Observable.of(
                mapRequestError(
                    error,
                    unauthenticated,
                    errorCreator,
                    item,
                    callbackActionCreator
                )
            )
        )
}

/*
 This is used for /stakeholder/:id/roles as the requestItemSaveHandler wasn't
 suitable for an API that has a subresource
*/
export const requestItemSaveSubresourceHandler = (
    patchItemAPI,
    putItemAPI,
    responseCreator,
    errorCreator
) => (getState, { id, item, callbackActionCreator }) => {
    const { token } = getState().authentication
    let result$ = selectSaveSubresourceAPI(
        id,
        item,
        token,
        putItemAPI,
        patchItemAPI
    )
    return result$
        .map(data => responseCreator(data))
        .catch(error =>
            Observable.of(
                mapRequestError(
                    error,
                    unauthenticated,
                    errorCreator,
                    item,
                    callbackActionCreator
                )
            )
        )
}

export const requestItemDeleteHandler = (
    deleteItemAPI,
    entityType,
    responseCreator,
    errorCreator
) => (getState, { id, callbackActionCreator }) => {
    const { token } = getState().authentication
    return Observable.combineLatest(deleteItemAPI(token, id))
        .map(_ => responseCreator(id, callbackActionCreator))
        .catch(error =>
            Observable.of(
                mapRequestError(
                    error,
                    unauthenticated,
                    errorCreator,
                    id,
                    callbackActionCreator
                )
            )
        )
}

export const requestItemDeleteSubresourceHandler = (
    deleteItemAPI,
    entityType,
    responseCreator,
    errorCreator
) => (getState, action) => {
    const { resourceID, subresourceID, callbackActionCreator } = action

    const { token } = getState().authentication

    let result$ = deleteItemAPI(token, resourceID, subresourceID)

    return result$
        .map(_ => responseCreator(subresourceID, callbackActionCreator))
        .catch(error =>
            Observable.of(
                mapRequestError(
                    error,
                    unauthenticated,
                    errorCreator,
                    subresourceID,
                    callbackActionCreator
                )
            )
        )
}

export const requestItemDeleteEpic = (
    type,
    deleteItemAPI,
    entityType,
    responseCreator,
    errorCreator
) => {
    const requestItemDelete = requestItemDeleteHandler(
        deleteItemAPI,
        entityType,
        responseCreator,
        errorCreator
    )
    return (action$, { getState }) =>
        action$
            .ofType(type)
            .mergeMap(action => requestItemDelete(getState, action))
}

function selectSaveAPI(id, item, token, putItemAPI, postItemAPI) {
    return id ? putItemAPI(token, id, item) : postItemAPI(token, item)
}

// for stakeholder/:id/roles
function selectSaveSubresourceAPI(id, item, token, putItemAPI, patchItemAPI) {
    return item.id ? patchItemAPI(token, id, item) : putItemAPI(token, id, item)
}

export const syncIdToURLEpic = (entityType, ...actionTypes) => action$ =>
    action$
        .ofType(...actionTypes)
        .filter(({ data }) => data && data.id)
        .map(({ data }) => push(`/${entityType}/${data.id}`))

export const callbackActionCreatorEpic = (...actionTypes) => action$ =>
    action$
        .ofType(...actionTypes)
        .filter(action => action.callbackActionCreator)
        .map(({ type, callbackActionCreator, ...rest }) =>
            callbackActionCreator(rest)
        )

export const removeOwnerViewerEpic = (
    presentationAccessor,
    removeType,
    updateModelActionCreator
) => (action$, { getState }) =>
    action$.ofType(removeType).map(({ id: removeId, ownerViewerType }) =>
        updateModelActionCreator(
            ownerViewerType,
            getState().presentation[presentationAccessor].workingModel[
                ownerViewerType
            ].filter(({ id }) => id !== removeId)
        )
    )
